/**
 * RemovalInstructionDisplay - Unified component for displaying removal instructions
 * Supports both format-level and pack-based removal campaigns
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Trash2, Package } from 'lucide-react';
import { getRemovalDisplayData, type RemovalDisplayData } from '@/utils/removal-display-helpers';

interface RemovalInstructionDisplayProps {
  campaign: {
    enableFormatLevelRemoval?: boolean;
    name?: string;
  };
  task: {
    packName?: string;
    formatId?: number;
    expectedCount?: number;
    mediaRequirements?: Array<{
      formatId: number;
      formatName: string;
      quantity: number;
    }>;
  };
  variant?: 'compact' | 'detailed' | 'card';
  className?: string;
}

export function RemovalInstructionDisplay({ 
  campaign, 
  task, 
  variant = 'compact', 
  className = '' 
}: RemovalInstructionDisplayProps) {
  const displayData = getRemovalDisplayData(campaign, task);

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Trash2 className="h-4 w-4 text-red-500" />
        <span className="font-medium text-red-700">{displayData.displayText}</span>
        {displayData.isFormatLevel && (
          <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
            Format-Level
          </Badge>
        )}
      </div>
    );
  }

  if (variant === 'detailed') {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-red-500" />
          <h3 className="font-semibold text-red-700">Removal Instructions</h3>
          {displayData.isFormatLevel ? (
            <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
              Format-Level Removal
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
              Pack Removal
            </Badge>
          )}
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="font-medium text-red-800 mb-2">{displayData.displayText}</p>
          
          {displayData.isFormatLevel && displayData.formats && (
            <div className="space-y-1">
              <p className="text-sm text-red-600 font-medium">Format breakdown:</p>
              <ul className="space-y-1">
                {displayData.formats.map((format, index) => (
                  <li key={`${format.formatId}-${index}`} className="text-sm text-red-700 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                    <span>{format.displayText}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {!displayData.isFormatLevel && displayData.packName && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <Package className="h-4 w-4" />
              <span>Pack: {displayData.packName}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className={`bg-white border border-red-200 rounded-lg p-4 space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-500" />
            <h4 className="font-semibold text-gray-900">Removal Task</h4>
          </div>
          
          {displayData.isFormatLevel ? (
            <Badge className="bg-red-100 text-red-700 border-red-300">
              Format-Level
            </Badge>
          ) : (
            <Badge className="bg-blue-100 text-blue-700 border-blue-300">
              Pack-Based
            </Badge>
          )}
        </div>
        
        <div className="space-y-2">
          <p className="font-medium text-red-800">{displayData.displayText}</p>
          
          {displayData.isFormatLevel && displayData.formats && displayData.formats.length > 1 && (
            <div className="bg-red-50 rounded-md p-3 space-y-1">
              <p className="text-sm font-medium text-red-700">Individual formats:</p>
              {displayData.formats.map((format, index) => (
                <div key={`${format.formatId}-${index}`} className="text-sm text-red-600 flex justify-between">
                  <span>{format.formatName}</span>
                  <span className="font-medium">{format.expectedCount} unit{format.expectedCount === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

/**
 * Hook for getting removal display data
 */
export function useRemovalDisplay(campaign: { enableFormatLevelRemoval?: boolean }, task: any): RemovalDisplayData {
  return React.useMemo(() => getRemovalDisplayData(campaign, task), [campaign.enableFormatLevelRemoval, task]);
}